"use client";
import type { PopularProductsStoryblok } from "@/generated/sb/sb-types";
import type { ProductCard as ProductCardType } from "@/lib/centra/formatters";

import { rpc } from "#rpc";
import BlokWrap from "@/components/ui/BlokWrap";
import { DepictRoute } from "@/data/routes/plp.route";
import { ProductCardList } from "@/features/product/components/ProductCardList";
import { Link } from "@/i18n/routing";
import { useToken } from "@frend-digital/centra/client";
import { BlokProps, getLink } from "@frend-digital/storyblok";
import { SbBlokData, storyblokEditable } from "@storyblok/js";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import styles from "./ProductListingBlok.module.css";

export const PopularProductsBlok = ({
  blok,
}: BlokProps<PopularProductsStoryblok, DepictRoute["recommendations"]>) => {
  const link = blok?.link?.at(0) ?? null;
  const href = getLink(link?.link)?.href;
  const token = useToken();

  const { data: products, isLoading } = useQuery({
    queryKey: ["product-cards", token],
    queryFn: async () => {
      const products = await rpc.depict.recommendations({
        limit: 8,
        type: "landing_page",
        token: token ?? "",
        product_ids: [],
      });

      return products?.filter((p) => !!p.depictProductId);
    },
    refetchOnWindowFocus: false,
    enabled: !!token,
    staleTime: Infinity,
  });

  if (isLoading) return null;

  return (
    <BlokWrap
      {...storyblokEditable(blok as unknown as SbBlokData)}
      animated="fadeY"
      className={clsx(styles.container, "product-listing")}
    >
      <div data-layout="page" className={styles.head}>
        {blok.title && <h2>{blok.title}</h2>}
        {link?.text && <Link href={href || "/"}>{link.text}</Link>}
      </div>

      {products?.length && (
        <ProductCardList cards={products?.slice(0, 4) as ProductCardType[]} />
      )}
    </BlokWrap>
  );
};
